import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { SelectOption } from "../Annotation/annotation.types";
import { ToastAnt } from "../../Shared/Toast/ToastAnt";
import axios from "../../../axios";
import { AppThunk } from "../../../store";

export type FailureModeCategoryTemplate = {
    id?: string;
    code: string;
    name: string;
    description: string;
    canDelete: boolean;
    canUpdate: boolean;
    deprecated: boolean;
}

const TAKE = 10;

type State = {
    failureModeCategoryTemplates: Array<FailureModeCategoryTemplate>;
    codes: SelectOption[];
    selectedCode?: string;
    names: SelectOption[];
    selectedName?: string;
    loading: boolean;
    formLoading: boolean;
    formResult?: boolean;
    totalNumber: number;
    currentPage: number;
    size: number;
};

const initialState: State = {
    failureModeCategoryTemplates: [],
    codes: [],
    names: [],
    loading: false,
    formLoading: false,
    totalNumber: 0,
    currentPage: 0,
    size: TAKE,
    selectedCode: undefined,
    selectedName: undefined
};

type FailureModeCategoryTemplateFiltersRequest = {
    codes: string[];
    names: string[];
};
  
type FailureModeCategoryTemplateFiltersResponse = {
    codes: SelectOption[];
    names: SelectOption[];
};

type PaggingConfiguration = {
    take: number;
    skip: number;
    getTotal: boolean;
    filters: FailureModeCategoryTemplateFiltersRequest;
  };
  
  type PaggingResult = {
    totalNumber?: number;
    elements: FailureModeCategoryTemplate[];
    filters: FailureModeCategoryTemplateFiltersResponse;
  };

const failureModeCategoryTemplateSlice = createSlice({
    name: 'failureModeCategoryTemplate',
    initialState,
    reducers: {
        startLoading(state) {
            state.loading = true;
          },
          endLoading(state) {
            state.loading = false;
          },
          startFormLoading(state) {
            state.formLoading = true;
          },
          endFormLoading(state) {
            state.formLoading = false;
          },
          setFormResult(state, action: PayloadAction<boolean | undefined>) {
            state.formResult = action.payload;
          },
          populatePaggingFields(state, action: PayloadAction<PaggingResult>) {
            const { elements, filters, totalNumber } = action.payload;
            state.failureModeCategoryTemplates = elements;
            state.codes = filters.codes;
            state.names = filters.names;
      
            if (totalNumber) {
              state.totalNumber = totalNumber;
            }
      
            state.loading = false;
          },
          setSizeAndPage: (state, action: PayloadAction<{ size: number; page: number }>) => {
            const { page, size } = action.payload;
            state.size = size;
            state.currentPage = page;
          },
          setSelectedCode(state, action: PayloadAction<string | undefined>) {
            state.selectedCode = action.payload;
          },
          setSelectedName(state, action: PayloadAction<string | undefined>) {
            state.selectedName = action.payload;
          }        
    }
});

export const {
    startLoading,
    endLoading,
    startFormLoading,
    endFormLoading,
    setFormResult,
    populatePaggingFields,
    setSizeAndPage,
    setSelectedCode,
    setSelectedName
} = failureModeCategoryTemplateSlice.actions;

export default failureModeCategoryTemplateSlice.reducer;

export const getFailureModeCategoryTemplates =
  (take: number, newPage: number): AppThunk =>
  async (dispatch, getState) => {
    const { selectedCode, selectedName } = getState().failureModeCategoryTemplate;

    dispatch(startLoading());

    dispatch(setSizeAndPage({ page: newPage, size: take }));

    const config: PaggingConfiguration = {
      take: take,
      skip: (newPage - 1) * take,
      getTotal: true,
      filters: {
        codes: selectedCode ? [selectedCode] : [],
        names: selectedName ? [selectedName] : []
      }
    };

    try {
      const response = await axios.post<PaggingResult>('/failureModeCategory/search', config);
      dispatch(populatePaggingFields(response.data));
    } catch (error) {
      console.log(error);
    }

    dispatch(endLoading());
  };

export const createFailureModeCategoryTemplate =
  (template: FailureModeCategoryTemplate): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().failureModeCategoryTemplate;

    dispatch(startFormLoading());

    try {
      await axios.post('/failureModeCategory', template);

      dispatch(getFailureModeCategoryTemplates(size, 1));
      dispatch(setFormResult(true));
      ToastAnt('Saved.');
    } catch (error) {
      handleError(error);
      dispatch(setFormResult(false));
    }

    dispatch(endFormLoading());
  };

export const updateFailureModeCategoryTemplate =
  (template: FailureModeCategoryTemplate): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().failureModeCategoryTemplate;

    dispatch(startFormLoading());

    try {
      await axios.put('/failureModeCategory', template);

      dispatch(getFailureModeCategoryTemplates(size, 1));
      dispatch(setFormResult(true));
      ToastAnt('Saved.');
    } catch (error) {
      dispatch(setFormResult(false));
      handleError(error);
    }

    dispatch(endFormLoading());
  };

export const deleteFailureModeCategoryTemplate =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().failureModeCategoryTemplate;

    dispatch(startLoading());

    try {
      await axios.delete(`/failureModeCategory/${id}`);

      dispatch(getFailureModeCategoryTemplates(size, 1));
      ToastAnt('Deleted.');
    } catch (error) {
      console.log(error);
      ToastAnt('An error occurred.');
    }

    dispatch(endLoading());
  };
function handleError(error: any) {
  console.log(error);
  if (error && error.response && error.response.data && error.response.data.Message) {
    ToastAnt(error.response.data.Message);
  } else {
    ToastAnt('An error occurred.');
  }
}